import * as React from "react"
import type { ReactNode } from "react"
import { Info } from "lucide-react"

import { cn } from "@/lib/utils"
import { t } from "@/lib/i18n"

interface WorkPanelProps {
  /** Titulo de la hoja. Corto: la tabla ya se explica sola. */
  title?: ReactNode
  /** Recuento u otro dato de cabecera, alineado a la derecha del titulo. */
  meta?: ReactNode
  actions?: ReactNode
  /**
   * Nota bajo la tabla: supuestos, reglas de lectura. PLEGADA por defecto; se
   * abre con el ⓘ de la cabecera. Ver «La nota se pide, no se impone».
   */
  footnote?: ReactNode
  children: ReactNode
  className?: string
  bodyClassName?: string
}

/**
 * Contenedor de una tabla de trabajo. La tabla vive dentro de una hoja con
 * filete; la cabecera de la hoja es una banda hundida que no se confunde con
 * la cabecera de columnas (que es sticky y tambien hundida, pero mas baja y en
 * versalitas). Sin esa distincion, al hacer scroll se pierde el contexto.
 *
 * ---------------------------------------------------------------------------
 * EL TITULO ES UN SUBTITULO, Y ANTES ERA UNA ETIQUETA
 *
 * Cesar, sobre el dashboard: «los titulos estan raros y no se ve tan limpio».
 * El diagnostico no era el tamano de ninguno: **faltaba un nivel entero**.
 *
 * La lamina 18 del brandbook fija cuatro roles: titulo (SemiBold, -3%, con
 * punto rojo), **subtitulo (Medium, sensiblemente menor, sin punto)**, cuerpo y
 * **etiqueta (mayusculas con interletrado, uso BREVE)**. Este titulo estaba
 * escrito como etiqueta —`text-2xs font-bold uppercase tracking-[0.09em]`—, asi
 * que el dashboard saltaba del titulo de pantalla de 17 px a una versalita de
 * 11 px sin nada en medio. Dos niveles haciendo el trabajo de tres: el ojo no
 * encuentra la jerarquia porque no esta.
 *
 * Ademas `font-bold` **no es un peso autorizado**: el brandbook admite Light,
 * Regular, Medium y SemiBold, «nunca Bold ni Black».
 *
 * Ahora es Medium a 13 px en caja baja. «Ingresos y EBITDA» es una frase, no un
 * rotulo de campo, y en versalitas una frase se lee letra a letra.
 *
 * ---------------------------------------------------------------------------
 * LA NOTA SE PIDE, NO SE IMPONE
 *
 * Cesar, 2026-10-04: «reduciendo texto para que se vea mas limpio». Habia
 * setenta y seis notas al pie abiertas en el producto, y una nota al pie que
 * siempre esta abierta es un parrafo mas en cada pantalla: la primera vez se
 * lee, la segunda se salta, y a partir de ahi solo ocupa. El texto NO se borra
 * —explica supuestos que importan— pero pasa a estar detras de un ⓘ en la
 * cabecera: quien quiere saber como se lee la tabla lo abre; quien ya lo sabe
 * ve la tabla.
 *
 * La preferencia no se guarda a proposito: es una pregunta puntual («como se
 * lee esto?»), no un ajuste. Guardarla por panel seria un localStorage por
 * tabla para ahorrar un clic.
 */
export function WorkPanel({
  title,
  meta,
  actions,
  footnote,
  children,
  className,
  bodyClassName,
}: WorkPanelProps) {
  const [notaAbierta, setNotaAbierta] = React.useState(false)
  const hayCabecera = Boolean(title || meta || actions || footnote)

  return (
    <section data-guia="panel" className={cn("overflow-hidden rounded-panel border bg-surface shadow-xs", className)}>
      {hayCabecera ? (
        <header className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b bg-surface-sunken/60 px-2.5 py-2">
          {title ? (
            <h2 className="text-sm font-medium leading-tight text-foreground">{title}</h2>
          ) : null}
          {footnote ? (
            <button
              type="button"
              onClick={() => setNotaAbierta((v) => !v)}
              aria-expanded={notaAbierta}
              aria-label={t("Cómo se lee")}
              title={t("Cómo se lee")}
              className={cn(
                "-ml-1.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground",
                "transition-colors hover:bg-accent hover:text-foreground",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                notaAbierta && "bg-accent text-foreground",
              )}
            >
              <Info className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          ) : null}
          {meta ? <div className="flex items-center gap-1.5">{meta}</div> : null}
          {actions ? <div className="ml-auto flex items-center gap-1.5">{actions}</div> : null}
        </header>
      ) : null}
      <div className={cn("min-w-0", bodyClassName)}>{children}</div>
      {footnote && notaAbierta ? (
        <footer className="border-t bg-surface-sunken/40 px-2.5 py-1.5 text-2xs leading-4 text-muted-foreground">
          {footnote}
        </footer>
      ) : null}
    </section>
  )
}
